import React, { useContext, useState } from 'react';

import LockOutlineIcon from '@mui/icons-material/LockOutline';
import { Box, Typography, useTheme } from '@mui/material';

import axiosApiInstance from '@/api/shared/axios-instance';
import { REDUCERS } from '@/constants/reducers';
import { Context } from '@/context';
import { translate } from '@/data/fixture-transalations';
import { getTimeShortWithMinutesLong } from '@/helpers/datetime-helper';
import URL_FACTORY from '@/helpers/url-helper';
import NavigationButton from './NavigationButton';
import CustomModal from '@/DesignSystem/components/custom-modal/CustomModal';
import CustomTooltip from '@/DesignSystem/components/tooltip/CustomTooltip';

const FechasClausulables = () => {
    const { dispatch } = useContext(Context);
    const [fechasClausulables, setFechasClausulables] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);

    const theme = useTheme();

    // Obtener fecha del próximo partido
    const getNextMatchDate = () => {
        if (!fechasClausulables || Object.keys(fechasClausulables).length === 0) {
            return 'No disponible';
        }

        for (const key of Object.keys(fechasClausulables)) {
            const jornadas = fechasClausulables[key];

            if (Array.isArray(jornadas)) {
                const jornadaValida = jornadas.find((jornada) => jornada?.round && jornada?.date);

                if (jornadaValida) {
                    return getTimeShortWithMinutesLong(jornadaValida.date);
                }
            }
        }

        return 'No disponible';
    };

    // Obtener fecha de cierre de cláusulas (24 horas antes)
    const getClauseClosingDate = () => {
        if (!fechasClausulables || Object.keys(fechasClausulables).length === 0) {
            return 'No disponible';
        }

        const firstKey = Object.keys(fechasClausulables)[0];
        const firstItem = fechasClausulables[firstKey]?.[0];

        if (firstItem?.date) {
            const matchDate = new Date(firstItem.date);
            matchDate.setHours(matchDate.getHours() - 24); // Restar 24 horas
            return getTimeShortWithMinutesLong(matchDate.toISOString());
        }

        return 'No disponible';
    };

    const fetchFechasClausulables = async () => {
        if (fechasClausulables && Object.keys(fechasClausulables).length > 0) {
            setModalOpen(true);
            return;
        }

        dispatch({ type: REDUCERS.SHOW_SPINNER });

        axiosApiInstance
            .get(URL_FACTORY.GetFechasClausulables)
            .then((response) => {
                setFechasClausulables(response.data.datesWithRounds);
                setModalOpen(true);
            })
            .catch((error) => {
                console.error('Error fetching clausulas:', error);
            })
            .finally(() => {
                dispatch({ type: REDUCERS.HIDE_SPINNER });
            });
    };

    const handleClose = () => {
        setModalOpen(false);
    };

    return (
        <>
            <NavigationButton onClick={fetchFechasClausulables} aria-label="Ver fechas clausulables">
                <CustomTooltip label="Fechas clausulables">
                    <LockOutlineIcon sx={{ color: theme.palette.footer.text }} />
                </CustomTooltip>
            </NavigationButton>

            {modalOpen && (
                <CustomModal open={modalOpen} onClose={handleClose} title="Fechas clausulables">
                    <Box
                        sx={{
                            overflowY: 'auto',
                            flex: 1,
                        }}
                    >
                        <Typography variant="body2" mb={1}>
                            Se muestra una simulación, si fichas a un jugador en una de estas fechas, los
                            rivales no te lo podrán quitar por clausulazos.
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '2px',
                                marginBottom: '16px',
                                borderRadius: '4px',
                                flexDirection: 'column',
                                bgcolor: theme.palette.background.darkCard,
                                p: 1,
                            }}
                        >
                            <Box display={'flex'} gap={'4px'}>
                                <Typography variant="subtitle1">
                                    📅 Próximo partido: <strong>{getNextMatchDate()}</strong>
                                </Typography>
                            </Box>

                            <Box display={'flex'} gap={'4px'}>
                                <LockOutlineIcon color="secondary" />
                                <Typography variant="subtitle1">
                                    Cierre de cláusulas: <strong>{getClauseClosingDate()}</strong>
                                </Typography>
                            </Box>
                        </Box>

                        {fechasClausulables && Object.keys(fechasClausulables).length > 0 ? (
                            Object.entries(fechasClausulables).map(([fecha, jornadas]) => {
                                if (!jornadas || jornadas.length === 0) {
                                    return null;
                                }

                                return (
                                    <Box
                                        key={fecha}
                                        sx={{
                                            marginBottom: '12px',
                                            borderRadius: '4px',
                                            bgcolor: theme.palette.background.darkCard,
                                            p: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ fontWeight: 'bold', marginBottom: '4px' }}
                                        >
                                            📅 {getTimeShortWithMinutesLong(fecha)}
                                        </Typography>
                                        {jornadas.map((jornada, index) => (
                                            <Typography key={index} variant="body2">
                                                <LockOutlineIcon color="primary" /> {translate(jornada.round)}{' '}
                                                - {getTimeShortWithMinutesLong(jornada.date)}
                                            </Typography>
                                        ))}
                                    </Box>
                                );
                            })
                        ) : (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ textAlign: 'center', marginTop: '20px' }}
                            >
                                No hay fechas disponibles para mostrar.
                            </Typography>
                        )}
                    </Box>
                </CustomModal>
            )}
        </>
    );
};

export default FechasClausulables;
